/* eslint-disable no-console */
import type { User } from '@/api/user/types/user';
import { FirebaseCrashlytics } from '@capacitor-firebase/crashlytics';
import { datadogLogs } from '@datadog/browser-logs';

import environment from '@/environment';
import { datadogRum } from '@datadog/browser-rum';
import { Capacitor } from '@capacitor/core';
import { getAppVersionInfo, toRumAppInfoContext } from './app-version-info';
import { resolveTelemetryEnv } from './telemetry-env';
import { sanitizeLogEvent } from './telemetry-sanitizer';
const isDev = environment.isDev;

if (environment.isProd) {
  // Defer so Logs SDK setup does not contend with splash hide / first paint.
  window.setTimeout(() => {
    datadogLogs.init({
      clientToken: 'pub27eba693b23b720a7ac9c149106e8707',
      site: 'us5.datadoghq.com',
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      service: 'gyming-app',
      env: resolveTelemetryEnv(environment.name),
      version: environment.version,
      // Strips request/response bodies, credentials, emails and query values
      // from every log, including SDK-collected network and console errors.
      beforeSend: log => sanitizeLogEvent(log),
    });
    // Same native/OTA version context as RUM (one cached read per boot).
    void getAppVersionInfo()
      .then(info => datadogLogs.setGlobalContextProperty('app_info', toRumAppInfoContext(info)))
      .catch(() => undefined);
  }, 0);
}

export const setLoggerUser = async (user: User) => {
  if (!user) return;
  // Stable ID only: email and name are personal data Datadog does not need.
  datadogRum.setUser({ id: user.id });
  if (Capacitor.isNativePlatform()) {
    await FirebaseCrashlytics.setUserId({
      userId: user.id,
    });
    if (user.email) {
      await FirebaseCrashlytics.setCustomKey({
        key: 'email',
        value: user.email,
        type: 'string',
      });
    }
    if (user.profile.firstName) {
      await FirebaseCrashlytics.setCustomKey({
        key: 'name',
        value: user.profile.firstName,
        type: 'string',
      });
    }
  }
};

/**
 * Application context always travels under `ctx`, so it can never collide with
 * (or bypass the sanitizing of) fields the Datadog SDK owns, such as `status`,
 * `view` or `error`. Passing an array as the context would replace the whole
 * log event, which is why extra info arguments are wrapped too.
 */
const toLogContext = (ctx: unknown) => (ctx === undefined ? undefined : { ctx });

export default {
  info: (...message: unknown[]) => {
    if (isDev) {
      console.info(message);
    } else {
      const [text, ...rest] = message;
      datadogLogs.logger.info(String(text), toLogContext(rest.length > 1 ? rest : rest[0]));
    }
  },
  debug: (...message: unknown[]) => {
    if (isDev) {
      console.log(message);
    }
  },
  error: async ({ message, data, error }: { message: string; data?: object; error: Error }) => {
    if (isDev) {
      console.error(message, data, error);
    } else {
      datadogLogs.logger.error(message, toLogContext(data), error);
      FirebaseCrashlytics.recordException({
        message,
      });
    }
  },
};
